import React, { useEffect, useRef } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

// These must stay in step with .chat-composer-input in Chat.css. Line height is
// read from the element so the two can't silently drift.
const MAX_LINES = 5;
const FALLBACK_LINE_HEIGHT_PX = 22;
const VERTICAL_PADDING_PX = 22;

function ChatComposer({
  value,
  onChange,
  onSend,
  disabled,
  placeholder,
  maxLength,
  sendError,
  inputRef,
  onBackToAnswers,
}) {
  const localRef = useRef(null);
  const ref = inputRef || localRef;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const lineHeight =
      parseFloat(window.getComputedStyle(el).lineHeight) ||
      FALLBACK_LINE_HEIGHT_PX;
    const minHeight = lineHeight + VERTICAL_PADDING_PX;
    const maxHeight = lineHeight * MAX_LINES + VERTICAL_PADDING_PX;

    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)}px`;
    el.classList.toggle('is-scrollable', el.scrollHeight > maxHeight);
  }, [value, ref]);

  const handleKeyDown = (e) => {
    // isComposing guards IME input, where Enter commits a candidate rather than
    // submitting the message.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend(e);
    }
  };

  return (
    <div className="chat-composer">
      {onBackToAnswers && (
        <button
          type="button"
          className="chat-composer-back"
          onClick={onBackToAnswers}
        >
          <span aria-hidden="true">&larr;</span> Back to answers
        </button>
      )}
      <div className="chat-composer-row">
        <label className="sr-only" htmlFor="chat-composer-input">
          Message
        </label>
        <textarea
          id="chat-composer-input"
          ref={ref}
          className="chat-composer-input"
          placeholder={placeholder}
          value={value}
          maxLength={maxLength}
          rows={1}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-describedby={sendError ? 'chat-composer-error' : undefined}
          aria-invalid={sendError ? 'true' : undefined}
        />
        <button
          type="button"
          className="chat-composer-send"
          disabled={disabled || !value.trim()}
          onClick={onSend}
          aria-label="Send message"
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>
      </div>
      {sendError && (
        <p className="chat-composer-error" id="chat-composer-error" role="alert">
          {sendError}
        </p>
      )}
    </div>
  );
}

export default ChatComposer;
